import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  Grid,
  MenuItem,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { TenderComponentProps } from '../OCDTypes';

import useTender from './useTender';

const useStyles = makeStyles({
  type: {
    fontWeight: 700,
    fontSize: '16px',
  },
  dataRow: {
    margin: '0.25rem 0',
  },
  card: {
    '&:not(:last-child)': {
      borderBottomRightRadius: 0,
      borderBottomLeftRadius: 0,
      borderBottom: 'none',
    },
    '&:not(:first-child)': {
      borderTopRightRadius: 0,
      borderTopLeftRadius: 0,
    },
  },
});

function TenderMobile({
  onChange,
  isMissingReason,
  name,
  type,
  expected,
  counted,
  reason,
  isFirst,
  billsTotal,
}: TenderComponentProps) {
  const { t } = useTranslation('openCloseDay', {
    keyPrefix: 'closeDay.tenders',
  });
  const styles = useStyles();

  const {
    showExpected,
    countedProps,
    expectedProps,
    differenceProps,
    reasonProps,
  } = useTender({
    onChange,
    isMissingReason,
    type,
    expected,
    counted,
    reason,
    isFirst,
    billsTotal,
  });

  return (
    <Card variant="outlined" className={styles.card}>
      <Box padding="0.875rem">
        <Box display="flex" gridGap="0.5rem">
          <Typography
            data-testid="input-header"
            data-test-key={type}
            className={styles.type}
          >
            {t(`tenders.${type}`, name)}
          </Typography>
        </Box>

        <Box display="flex" flexWrap="wrap">
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            margin="dense"
            data-testid="input"
            data-test-key={type}
            label={t('headers.counted')}
            name={type}
            className={countedProps.className}
            value={countedProps.value}
            placeholder={countedProps.placeholder}
            onChange={countedProps.onChange}
            error={countedProps.error}
          />

          {showExpected && (
            <>
              <Grid container className={styles.dataRow}>
                <Grid item xs={6}>
                  <Typography
                    data-testid="expected-header"
                    color="textSecondary"
                  >
                    {t('headers.expected')}:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    data-testid="expected"
                    data-test-key={type}
                    align="right"
                  >
                    {expectedProps.value}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container className={styles.dataRow}>
                <Grid item xs={6}>
                  <Typography
                    data-testid="difference-header"
                    color="textSecondary"
                  >
                    {t('headers.difference')}:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography data-testid="diff" align="right">
                    {differenceProps.value}
                  </Typography>
                </Grid>
              </Grid>

              <TextField
                size="small"
                fullWidth
                variant="outlined"
                margin="dense"
                data-testid="reason-select"
                data-test-key={type}
                label={t('headers.reason')}
                select
                value={reasonProps.value}
                onChange={reasonProps.onChange}
                error={reasonProps.error}
              >
                {reasonProps.options.map(({ name, reasonID }) => (
                  <MenuItem key={reasonID} value={reasonID}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
        </Box>
      </Box>
    </Card>
  );
}

export default TenderMobile;
